import React, { useState, useMemo, useCallback } from 'react';
import { MapPin, Clock, Navigation, CheckCircle, Eye, Loader2 } from 'lucide-react';
import FilterBar from './FilterBar';
import VisitDetailsModal from './VisitDetailsModal';
import StaffDateGate, { gateScopeParams } from './StaffDateGate';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 100;

const STATUS_TONES = {
  ongoing: 'bg-orange-100 text-orange-700',
  completed: 'bg-green-100 text-green-700',
  approved: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-red-100 text-red-700',
  completed_pending_approval: 'bg-amber-100 text-amber-700'
};

const STATUS_OPTIONS = [
  { value: 'ongoing', label: 'Ongoing' },
  { value: 'completed', label: 'Completed' },
  { value: 'completed_pending_approval', label: 'Pending Approval' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' }
];

export default function AllVisits() {
  const [visits, setVisits] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [vehicleFilter, setVehicleFilter] = useState('all');
  const [dateRange, setDateRange] = useState({ from: '', to: '' });
  const [gate, setGate] = useState({ geo_fence_id: null, geo_fence_name: null, staff: null, from: '', to: '' });
  const [openVisitId, setOpenVisitId] = useState(null);

  const fetchPage = useCallback(async (skip) => {
    const scope = gateScopeParams(gate);
    if (!scope) return { data: [], total: 0 };
    const token = localStorage.getItem('token');
    const params = new URLSearchParams({
      skip: String(skip),
      limit: String(PAGE_SIZE),
      ...scope,
    });
    const res = await fetch(`${API_URL}/api/admin/visits?${params.toString()}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    const data = await res.json();
    const totalHeader = parseInt(res.headers.get('X-Total-Count') || '0', 10);
    return { data, total: totalHeader || data.length };
  }, [gate.geo_fence_id, gate.staff?.id, gate.from, gate.to]);

  const applyGate = async () => {
    setLoading(true); setHasLoaded(true);
    try {
      const { data, total: t } = await fetchPage(0);
      setVisits(data);
      setTotal(t);
    } catch (err) {
      console.error('Failed to fetch visits:', err);
    } finally { setLoading(false); }
  };

  const loadMore = async () => {
    if (loadingMore || visits.length >= total) return;
    setLoadingMore(true);
    try {
      const { data } = await fetchPage(visits.length);
      setVisits(prev => [...prev, ...data]);
    } catch (err) {
      console.error('Failed to load more:', err);
    } finally {
      setLoadingMore(false);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return visits.filter(v => {
      if (statusFilter !== 'all' && v.status !== statusFilter) return false;
      if (vehicleFilter !== 'all' && (v.vehicle_type || 'own') !== vehicleFilter) return false;
      if (dateRange.from || dateRange.to) {
        const t = v.start?.time || v.created_at;
        if (!t) return false;
        const day = new Date(t).toISOString().slice(0, 10);
        if (dateRange.from && day < dateRange.from) return false;
        if (dateRange.to && day > dateRange.to) return false;
      }
      if (q && !(
        (v.staff_name || '').toLowerCase().includes(q) ||
        (v.supplier_name || '').toLowerCase().includes(q) ||
        (v.purpose || '').toLowerCase().includes(q)
      )) return false;
      return true;
    });
  }, [visits, search, statusFilter, vehicleFilter, dateRange]);

  if (loading) return <div className="text-center py-8">Loading visits...</div>;

  const hasMore = visits.length < total;

  return (
    <div data-testid="all-visits-page">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold text-gray-900">All Visits</h1>
        <div className="text-sm text-gray-500" data-testid="all-visits-count">
          {hasLoaded && (visits.length === total ? `${total} total` : `${visits.length} of ${total} loaded`)}
        </div>
      </div>

      <div className="mb-4">
        <StaffDateGate value={gate} onChange={setGate} onApply={applyGate} loading={loading} defaultDays={7} />
      </div>

      {!hasLoaded && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-8 text-center text-blue-700" data-testid="all-visits-prompt">
          <Eye className="mx-auto mb-2 opacity-60" size={28} />
          <p className="font-medium">Pick a staff and date range, then tap <strong>Load</strong>.</p>
          <p className="text-xs text-blue-600 mt-1">Loads only this staff's visits in the date window — instant even on production scale.</p>
        </div>
      )}

      {hasLoaded && (
        <FilterBar
          testId="visit-filters"
          placeholder="Search by supplier or purpose…"
          search={search}
          onSearch={setSearch}
          chips={[
            { key: 'status', label: 'Status', value: statusFilter, options: STATUS_OPTIONS },
            { key: 'vehicle', label: 'Vehicle', value: vehicleFilter, options: [
              { value: 'own', label: 'Own' }, { value: 'company', label: 'Company' }
            ]}
          ]}
          onChipChange={(k, v) => { if (k === 'status') setStatusFilter(v); else setVehicleFilter(v); }}
          dateRange={dateRange}
          onDateChange={setDateRange}
          onClear={() => { setSearch(''); setStatusFilter('all'); setVehicleFilter('all'); setDateRange({ from: '', to: '' }); }}
          resultCount={filtered.length}
        />
      )}

      {hasLoaded && (
      <div className="space-y-4">
        {filtered.map(visit => (
          <div
            key={visit.id}
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:border-blue-300 hover:shadow-md transition cursor-pointer"
            onClick={() => setOpenVisitId(visit.id)}
            data-testid={`visit-row-${visit.id}`}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <h3 className="font-semibold text-lg text-gray-900">{visit.supplier_name}</h3>
                <p className="text-sm text-gray-600 mt-1">By {visit.staff_name}</p>
                <p className="text-xs text-gray-500 mt-1">{visit.purpose || '—'}</p>
              </div>
              <div className="flex items-center space-x-2">
                <span className={`px-3 py-1 text-xs font-medium rounded-full capitalize ${STATUS_TONES[visit.status] || 'bg-gray-100 text-gray-700'}`}>
                  {(visit.status || '').replaceAll('_', ' ')}
                </span>
                <Eye size={18} className="text-blue-600" />
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <Stat icon={<Clock size={16} className="text-blue-600" />} label="Duration" value={visit.duration_hours ? `${visit.duration_hours}h` : '-'} />
              <Stat icon={<Navigation size={16} className="text-purple-600" />} label="Distance" value={visit.distance_km ? `${visit.distance_km} km` : '-'} />
              <Stat icon={<MapPin size={16} className="text-green-600" />} label="Started" value={visit.start?.time ? new Date(visit.start.time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '-'} />
              <Stat icon={<CheckCircle size={16} className="text-orange-600" />} label="Vehicle" value={(visit.vehicle_type || 'own') === 'own' ? 'Own' : 'Company'} />
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="bg-white rounded-xl p-8 text-center text-gray-500">{visits.length === 0 ? 'No visits yet' : 'No visits match the current filter'}</div>
        )}
      </div>
      )}

      {hasLoaded && hasMore && (
        <div className="flex justify-center mt-6">
          <button
            onClick={loadMore}
            disabled={loadingMore}
            className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-full shadow-sm disabled:opacity-60"
            data-testid="all-visits-load-more"
          >
            {loadingMore ? <Loader2 size={14} className="animate-spin" /> : null}
            {loadingMore ? 'Loading…' : `Load more (${total - visits.length} remaining)`}
          </button>
        </div>
      )}

      {openVisitId && (
        <VisitDetailsModal visitId={openVisitId} onClose={() => setOpenVisitId(null)} />
      )}
    </div>
  );
}

function Stat({ icon, label, value }) {
  return (
    <div className="flex items-center space-x-2">
      {icon}
      <div>
        <p className="text-xs text-gray-500">{label}</p>
        <p className="font-medium text-xs">{value}</p>
      </div>
    </div>
  );
}
